import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Select } from 'antd';
import { useGetClientContextQuery } from '../../features/platform/platformApi';
import {
  useGetResourcesQuery,
  useGetResourceFiltersQuery,
  useDeleteResourceMutation,
} from '../../features/resources/resourcesApi';

/* ------------------------------------------------------------------ *
 * Client Admin — Resources.
 * Source data the client collects (hospitals, schools, marriage halls, …),
 * scoped to the client's org. Used later as dropdown data. List + delete here;
 * add/edit open the shared resource form (AddResourcePage).
 * ------------------------------------------------------------------ */

// Common categories to offer even before any are saved (merged with the API's list).
const DEFAULT_CATEGORIES = ['Hospital', 'School', 'College', 'Marriage Hall', 'Religious Place', 'Bank', 'Police Station', 'Other'];

const LABEL = 'text-xs font-semibold text-ink-secondary';

export default function ClientResourcesPage() {
  const navigate = useNavigate();
  const { data: ctx } = useGetClientContextQuery();
  const orgId = ctx?.organization_id;
  // Return here + scope new resources to the client's org when opening the shared form.
  const formQuery = `returnTo=/client/resources${orgId != null ? `&org=${orgId}` : ''}`;

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>('');
  const { data, isLoading } = useGetResourcesQuery(
    { organization: orgId, search: search || undefined, category: category || undefined, pageSize: 100 },
    { skip: !orgId, refetchOnMountOrArgChange: true },
  );
  const { data: filters } = useGetResourceFiltersQuery();
  const resources = data?.items ?? [];
  const [deleteResource] = useDeleteResourceMutation();

  const categoryOptions = useMemo(() => {
    const set = new Set([...DEFAULT_CATEGORIES, ...(filters?.categories ?? [])]);
    return Array.from(set).sort();
  }, [filters]);

  return (
    <div className="flex flex-col gap-[18px]">
      <section className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-brand">CLIENT SETUP · SOURCE DATA</span>
          <h1 className="text-2xl font-semibold leading-9 tracking-[-0.75px] text-ink">Resources</h1>
          <p className="max-w-[780px] text-sm leading-6 text-ink-secondary">
            Collect source data for {ctx?.organization_name ?? 'your organization'} — hospitals, schools,
            marriage halls and more. It becomes available as dropdown data wherever a resource is needed.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate(`/client/resources/new?${formQuery}`)}
          className="h-10 shrink-0 rounded-lg bg-brand px-5 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark"
        >
          + Add resource
        </button>
      </section>

      {/* Filters */}
      <section className="flex flex-wrap items-end gap-2.5 rounded-xl border border-line/30 bg-white p-4 shadow-card">
        <label className="flex min-w-[220px] flex-1 flex-col gap-1">
          <span className={LABEL}>Search</span>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or address" allowClear />
        </label>
        <label className="form-field-select flex min-w-[200px] flex-col gap-1">
          <span className={LABEL}>Category</span>
          <Select
            value={category || undefined}
            onChange={(v) => setCategory(v ?? '')}
            placeholder="All categories"
            allowClear
            options={categoryOptions.map((c) => ({ value: c, label: c }))}
          />
        </label>
      </section>

      {/* List */}
      <section className="overflow-hidden rounded-xl border border-line/30 bg-white shadow-card">
        <div className="overflow-x-auto">
          <div className="min-w-[720px]">
            <div className="grid grid-cols-[minmax(200px,1.6fr)_140px_minmax(200px,1.4fr)_130px_120px] gap-3 border-b border-[#f3f4f6] bg-[#f9fafb] px-5 py-2.5 text-[11px] uppercase tracking-[0.05em] text-ink-secondary">
              <span>Name</span>
              <span>Category</span>
              <span>Address</span>
              <span>Contact</span>
              <span className="text-right">Actions</span>
            </div>
            {isLoading && <div className="px-5 py-4 text-[13px] text-ink-secondary">Loading resources…</div>}
            {!isLoading && resources.length === 0 && (
              <div className="px-5 py-4 text-[13px] text-ink-secondary">
                No resources yet. Add your first one.
              </div>
            )}
            {resources.map((r) => (
              <div key={r.id} className="grid grid-cols-[minmax(200px,1.6fr)_140px_minmax(200px,1.4fr)_130px_120px] items-center gap-3 border-b border-[#f3f4f6] px-5 py-3 last:border-b-0">
                <span className="flex min-w-0 flex-col">
                  <b className="truncate text-sm text-ink">{r.name}</b>
                  {r.sub_category && <span className="text-xs text-ink-secondary">{r.sub_category}</span>}
                </span>
                <span className="text-xs font-medium text-ink-secondary">{r.category || '—'}</span>
                <span className="truncate text-xs text-ink-secondary">{r.address || '—'}</span>
                <span className="text-xs text-ink-secondary">{r.mobile || '—'}</span>
                <span className="flex items-center justify-end gap-1.5">
                  <button
                    type="button"
                    onClick={() => navigate(`/client/resources/${r.id}/edit?${formQuery}`)}
                    className="h-9 rounded-lg border border-line bg-white px-3 text-xs font-semibold text-ink transition-colors hover:bg-brand-shade"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => void deleteResource(r.id)}
                    className="h-9 rounded-lg border border-[#fecaca] bg-white px-3 text-xs font-semibold text-[#dc2626] transition-colors hover:bg-[#fef2f2]"
                  >
                    Delete
                  </button>
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
